import {RefSelector} from '@github-ui/ref-selector'
import {ReposSelector} from '@github-ui/repos-selector'
import {Button, FormControl, Banner, IconButton} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'
import {useCallback, useRef, useState, useImperativeHandle, useMemo, useId} from 'react'
import {GitBranchIcon, GitBranchCheckIcon} from '@primer/octicons-react'
import {IconButtonSelector} from '@github-ui/copilot-chat/components/IconButtonSelector'
import {useEntitlement} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {CopilotPlan} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {
  COPILOT_ASSIGNMENT_PROMPT_PLACEHOLDER,
  COPILOT_INSTRUCTIONS_MAX_LENGTH,
  COPILOT_ASSIGNMENT_BRANCH_CAPTION,
  COPILOT_ASSIGNMENT_REPO_LABEL,
  COPILOT_ASSIGNMENT_BRANCH_LABEL,
  COPILOT_ASSIGNMENT_AGENT_LABEL,
  COPILOT_ASSIGNMENT_PROMPT_LABEL,
  COPILOT_ASSIGNMENT_SELECT_BRANCH_HEADING,
  AGENT_ASSIGNMENT_PROMPT_PLACEHOLDER,
  AGENT_ASSIGNMENT_BRANCH_CAPTION,
} from '../../utils/constants'
import {
  useCopilotRepositoryFetching,
  type DropdownRepository,
  type SimpleRepository,
} from '../../hooks/useCopilotRepoFetching'
import {useDefaultOwnerAwareRepositoryLoader} from '../../hooks/useDefaultOwnerAwareRepositoryLoader'
import styles from './CopilotAssignmentForm.module.css'
import {CommandButton} from '@github-ui/ui-commands'
import {AgentSelector} from '../new-task-chat-input/AgentSelector'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ModelPicker} from '../task-view/ModelPicker'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import type {SelectedAgent} from '../../types/session'
import {agentSupportsModelSelection} from '../../utils/agent-helpers'
import {useLastSelectedAgent} from '../../hooks/use-last-selected-agent'
import {useAgentVendorModel} from '../../hooks/use-agent-vendor-model'
import {useTopLevelAgents} from '../../hooks/useTopLevelAgents'
import {DecoupledModeBanner, DISMISSAL_KEY} from '../DecoupledModeBanner'
import {useNoticeDismissal} from '../../hooks/use-notice-dismissal'

const CROSS_ORG_WARNING_TEXT_AGENT =
  "You're assigning the agent to perform work in a different organization. The issue description will be visible to users outside this repository."
const PRIVATE_TO_PUBLIC_WARNING_TEXT_AGENT =
  "You're assigning the agent to perform work in a public repository. The issue description will be visible to users outside this repository."
const EMPTY_REPO_WARNING_TEXT =
  "The selected repo has no commits or branches so there's no valid base branch to create a PR against."

export interface CopilotAssignmentFormRef {
  submit: () => Promise<void>
}

export interface CopilotAssignmentFormProps {
  initialRepo: SimpleRepository
  onSubmit: (
    content: string,
    selectedRepo: DropdownRepository | null,
    selectedBranch: string,
    selectedAgent: SelectedAgent,
    selectedModel: string | undefined,
  ) => Promise<void>
  onCancel?: () => void
  isSubmitting?: boolean
  isQuotaExhausted?: boolean
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>
  textareaAriaDescribedBy?: string
  nested?: boolean
  ref?: React.Ref<CopilotAssignmentFormRef>
  promptPlaceholder?: string
  promptLabelHidden?: boolean
  promptRequired?: boolean
  insideDialog?: boolean
  initialAgent?: SelectedAgent
}

export function CopilotAssignmentForm({
  ref,
  initialRepo,
  onSubmit,
  onCancel,
  isSubmitting = false,
  isQuotaExhausted = false,
  textareaRef,
  textareaAriaDescribedBy,
  promptPlaceholder,
  promptLabelHidden = false,
  promptRequired = false,
  insideDialog = false,
  initialAgent,
}: CopilotAssignmentFormProps) {
  const {plan} = useEntitlement()
  const {data: noticeDismissed} = useNoticeDismissal(DISMISSAL_KEY.issues)
  const [content, setContent] = useState('')
  const [textareaValidationError, setTextareaValidationError] = useState<string | undefined>(undefined)
  const [branchValidationError, setBranchValidationError] = useState<string | undefined>(undefined)

  const [selectedAgent, setSelectedAgent] = useLastSelectedAgent(initialAgent)

  // Only fetch the top-level agents list when the selected agent needs enrichment
  // (missing modelVendor). This avoids unnecessary requests in tests and when
  // the agent was selected from AgentListItems (which already sets modelVendor).
  const needsVendorEnrichment = selectedAgent.typeName === 'topLevel' && !selectedAgent.modelVendor
  const initialRepoNameWithOwner = `${initialRepo.owner.login}/${initialRepo.name}`
  const {data: topLevelAgents = []} = useTopLevelAgents(needsVendorEnrichment ? initialRepoNameWithOwner : undefined)

  // Enrich selectedAgent with modelVendor from the agents list when it's missing
  // (e.g., when coming from the issue assignee picker which doesn't have vendor info).
  const enrichedAgent = useMemo(() => {
    if (needsVendorEnrichment && topLevelAgents.length > 0) {
      const matched = topLevelAgents.find(
        a => a.bot_global_relay_id === selectedAgent.botGlobalRelayId || a.slug === selectedAgent.login,
      )
      if (matched?.model_vendor) {
        return {...selectedAgent, modelVendor: matched.model_vendor}
      }
    }
    return selectedAgent
  }, [selectedAgent, topLevelAgents, needsVendorEnrichment])

  const {
    agentSlug,
    vendorFilter,
    selectedModelId: selectedModel,
    setSelectedModelId: setSelectedModel,
  } = useAgentVendorModel(enrichedAgent)

  // Generate a unique cache key per modal mount to prevent stale branch data
  const cacheKey = `copilot-assignment-ref-selector-${useId()}`

  const internalTextareaRef = useRef<HTMLTextAreaElement>(null)
  const finalTextareaRef = textareaRef || internalTextareaRef

  // Create defaultOwner-aware repository loader that ensures initial repo is always fetched
  const defaultOwnerAwareRepositoryLoader = useDefaultOwnerAwareRepositoryLoader(
    initialRepo.owner.login,
    initialRepoNameWithOwner,
  )

  // Use the shared repository fetching hook
  const {
    selectedRepo,
    selectedBranch,
    setSelectedBranch,
    isLoadingBranch,
    initialRepoApiMetadata,
    handleRepoSelect: handleRepoSelectFromHook,
    handleReposLoaded,
  } = useCopilotRepositoryFetching(initialRepo)

  const isSweSubagentsEnabled = isFeatureEnabled('copilot_swe_agent_use_subagents')
  const useAgentLabels = isFeatureEnabled('issue_cca_modal_open')

  const warningMessage = useMemo(() => {
    if (!selectedRepo) return null

    const copilotAgentName = isFeatureEnabled('cca_rename') ? 'Copilot cloud agent' : 'Copilot coding agent'
    const targetIsPublic = !selectedRepo.private
    const differentOrganization = initialRepo.owner.login !== selectedRepo.owner

    if (selectedRepo.isEmpty) {
      return EMPTY_REPO_WARNING_TEXT
    }

    if (differentOrganization) {
      return useAgentLabels
        ? CROSS_ORG_WARNING_TEXT_AGENT
        : `You're assigning ${copilotAgentName} to perform work in a different organization. The issue description will be visible to users outside this repository.`
    }

    if (initialRepoApiMetadata?.private && targetIsPublic) {
      return useAgentLabels
        ? PRIVATE_TO_PUBLIC_WARNING_TEXT_AGENT
        : `You're assigning ${copilotAgentName} to perform work in a public repository. The issue description will be visible to users outside this repository.`
    }

    return null
  }, [selectedRepo, initialRepo.owner.login, initialRepoApiMetadata, useAgentLabels])

  // Wrapper to prevent selection during submission
  const handleRepoSelect = useCallback(
    (repo: DropdownRepository | undefined) => {
      if (!repo || isSubmitting) return
      handleRepoSelectFromHook(repo)
      setBranchValidationError(undefined)
    },
    [isSubmitting, handleRepoSelectFromHook],
  )

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return

    setTextareaValidationError(undefined)
    setBranchValidationError(undefined)

    // Validate required prompt
    if (promptRequired && !content.trim()) {
      setTextareaValidationError('Please provide a prompt.')
      return
    }

    // Validate character limit
    if (content.length > COPILOT_INSTRUCTIONS_MAX_LENGTH) {
      const errorMessage = `Instructions must be ${COPILOT_INSTRUCTIONS_MAX_LENGTH.toLocaleString()} characters or fewer. Current length: ${content.length.toLocaleString()} characters.`
      setTextareaValidationError(errorMessage)
      return
    }

    if (!selectedRepo || !selectedBranch) {
      setBranchValidationError('Please select a repository and branch.')
      return
    }

    if (selectedRepo.isEmpty) {
      setBranchValidationError('Please select a valid base branch.')
      return
    }

    await onSubmit(content, selectedRepo, selectedBranch, selectedAgent, selectedModel)
  }, [content, selectedRepo, selectedBranch, selectedAgent, selectedModel, onSubmit, isSubmitting, promptRequired])

  // Expose submit method through ref
  useImperativeHandle(
    ref,
    () => ({
      submit: handleSubmit,
    }),
    [handleSubmit],
  )

  const submitButtonInactive =
    isQuotaExhausted || selectedRepo?.isEmpty || isSubmitting || !selectedRepo || !selectedBranch
  const refSelectorInactive = isSubmitting || isLoadingBranch || !selectedRepo?.defaultBranch || selectedRepo?.isEmpty
  const customAgentPickerInactive = isSubmitting

  return (
    <div className={styles.formContainer}>
      {/* Label outside the input container */}
      {promptLabelHidden ? (
        <label htmlFor="copilot-assignment-prompt" className="sr-only">
          {COPILOT_ASSIGNMENT_PROMPT_LABEL}
        </label>
      ) : (
        <label htmlFor="copilot-assignment-prompt" className={styles.promptLabel}>
          {COPILOT_ASSIGNMENT_PROMPT_LABEL}
        </label>
      )}

      {/* Input container with embedded toolbar - matches ChatInput styling */}
      <div className={styles.inputContainer}>
        <div className={styles.textareaContainer}>
          <textarea
            id="copilot-assignment-prompt"
            ref={finalTextareaRef}
            className={styles.textarea}
            placeholder={
              promptPlaceholder ??
              (useAgentLabels ? AGENT_ASSIGNMENT_PROMPT_PLACEHOLDER : COPILOT_ASSIGNMENT_PROMPT_PLACEHOLDER)
            }
            value={content}
            onChange={e => setContent(e.target.value)}
            rows={2}
            aria-describedby={
              textareaValidationError
                ? [textareaAriaDescribedBy, 'copilot-assignment-prompt-error'].filter(Boolean).join(' ')
                : textareaAriaDescribedBy
            }
            maxLength={COPILOT_INSTRUCTIONS_MAX_LENGTH}
            disabled={isSubmitting}
          />
          {textareaValidationError && (
            <span id="copilot-assignment-prompt-error" className={styles.validationError}>
              {textareaValidationError}
            </span>
          )}
        </div>

        {/* Toolbar embedded inside the input container */}
        <div className={styles.toolbar}>
          <div className={styles.selectorsRow}>
            <FormControl className={styles.formControl}>
              <FormControl.Label visuallyHidden>{COPILOT_ASSIGNMENT_REPO_LABEL}</FormControl.Label>
              <ReposSelector
                repositoryLoader={defaultOwnerAwareRepositoryLoader}
                selectionVariant="single"
                selectAllOption={false}
                onSelect={handleRepoSelect}
                onLoad={handleReposLoaded}
                currentSelection={selectedRepo || undefined}
                buttonText={selectedRepo?.nameWithOwner || 'Select repository'}
                width="large"
                truncateMaxWidth={250}
                additionalButtonProps={{
                  variant: 'invisible',
                  className: styles.toolbarButton,
                  'aria-label': `Repository: ${selectedRepo?.nameWithOwner || 'Select repository'}`,
                  disabled: isSubmitting,
                }}
              />
            </FormControl>

            <FormControl className={styles.formControl}>
              <FormControl.Label visuallyHidden>{COPILOT_ASSIGNMENT_BRANCH_LABEL}</FormControl.Label>
              {selectedRepo || selectedBranch ? (
                <RefSelector
                  useFocusZone
                  cacheKey={cacheKey}
                  canCreate={false}
                  currentCommitish={selectedBranch || selectedRepo?.defaultBranch || ''}
                  defaultBranch={selectedRepo?.defaultBranch ?? selectedBranch}
                  owner={selectedRepo?.owner || ''}
                  repo={selectedRepo?.nameOnly || ''}
                  types={['branch']}
                  selectedRefType="branch"
                  closeOnSelect
                  hideShowAll
                  disabled={refSelectorInactive}
                  actionListHeadingText={COPILOT_ASSIGNMENT_SELECT_BRANCH_HEADING}
                  actionListDescription={
                    useAgentLabels ? AGENT_ASSIGNMENT_BRANCH_CAPTION : COPILOT_ASSIGNMENT_BRANCH_CAPTION
                  }
                  onSelectItem={selectedRef => {
                    if (!isSubmitting) {
                      setSelectedBranch(selectedRef)
                    }
                  }}
                  renderAnchor={props => (
                    <IconButtonSelector
                      {...props}
                      variant="invisible"
                      className={styles.toolbarButton}
                      icon={GitBranchIcon}
                      selectedIcon={GitBranchCheckIcon}
                      selected={selectedBranch !== selectedRepo?.defaultBranch}
                      aria-label={
                        selectedRepo?.isEmpty
                          ? 'Branch: None available'
                          : `Branch: ${isLoadingBranch ? 'Loading branch...' : selectedBranch || 'Select branch'}`
                      }
                      aria-labelledby={undefined}
                    />
                  )}
                  anchoredOverlayProps={{
                    variant: {regular: 'anchored', narrow: 'fullscreen'},
                    height: 'small',
                  }}
                />
              ) : (
                <IconButton
                  variant="invisible"
                  className={styles.toolbarButton}
                  icon={GitBranchIcon}
                  aria-label="Loading branches…"
                  disabled
                />
              )}
            </FormControl>
            {(isSweSubagentsEnabled || useAgentLabels) && selectedRepo && (
              <FormControl className={styles.formControl}>
                <FormControl.Label visuallyHidden>{COPILOT_ASSIGNMENT_AGENT_LABEL}</FormControl.Label>
                <AgentSelector
                  repo={selectedRepo}
                  branch={selectedBranch || selectedRepo.defaultBranch}
                  selectedAgent={selectedAgent}
                  onSelectAgent={agent => setSelectedAgent(agent)}
                  iconOnly
                  disabled={customAgentPickerInactive}
                  insideDialog={insideDialog}
                  buttonProps={{variant: 'invisible', className: styles.toolbarButton}}
                />
              </FormControl>
            )}
            {copilotFeatureFlags.codingAgentModelSelection &&
              (copilotFeatureFlags.codingAgentModelSelectionAllSkus ||
                plan === CopilotPlan.IndividualPro ||
                plan === CopilotPlan.IndividualProPlus ||
                plan === CopilotPlan.IndividualStudent) &&
              agentSupportsModelSelection(selectedAgent, agentSlug) && (
                <div className={styles.modelPickerContainer}>
                  <FormControl>
                    <FormControl.Label visuallyHidden>Model</FormControl.Label>
                    <ModelPicker
                      onUpdateModel={setSelectedModel}
                      selectedModelId={selectedModel}
                      vendorFilter={vendorFilter}
                    />
                  </FormControl>
                </div>
              )}
          </div>
        </div>
      </div>

      <DecoupledModeBanner
        className={styles.decoupledBanner}
        dismissalKey={DISMISSAL_KEY.issues}
        enabled={
          !(noticeDismissed?.dismissed ?? true) &&
          isFeatureEnabled('copilot_mission_control_decoupled_mode') &&
          isFeatureEnabled('issue_cca_visualization_decoupled_mode')
        }
      />

      {warningMessage && (
        <Banner variant="warning" title="Warning" hideTitle className={styles.warningBanner}>
          {warningMessage}
        </Banner>
      )}

      {branchValidationError && (
        <InlineMessage variant="critical" className={styles.branchValidation}>
          {branchValidationError}
        </InlineMessage>
      )}

      <div className={styles.buttonsContainer}>
        <div className={styles.buttonsRow}>
          {onCancel && (
            <Button variant="invisible" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <CommandButton
            commandId="github:submit-form"
            showKeybindingHint
            variant="primary"
            inactive={submitButtonInactive}
            loading={isSubmitting}
            className={styles.assignButton}
          >
            Assign
          </CommandButton>
        </div>
      </div>
    </div>
  )
}
